// The GET payload turned into the shape the panel holds.
//
// Tolerant by construction. The prompts are maintained in another repository
// and carry their own versions, so an unfamiliar field must not break a
// render, and a stage row that arrived without its student-facing object is an
// error to show rather than a crash.

import { TARA_FEEDBACK_STAGES } from './taraFeedbackGating';

const EMPTY = { phase: 'idle', data: null, status_label: null, error: null, startedAt: null };

// The server's start time as a local timestamp, falling back to now when the
// row does not carry one, which is what an older deployment returns.
function startedAtFrom(row) {
  const raw = row && row.started_at;
  if (typeof raw !== 'string' || !raw) return Date.now();
  const iso = /(Z|[+-]\d{2}:?\d{2})$/.test(raw) ? raw : `${raw}Z`;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return Date.now();
  // A clock skewed into the future would show a negative wait.
  return Math.min(t, Date.now());
}

export function taraFeedbackFromAttempt(payload) {
  const stages = {};
  const warnings = [];
  const source = (payload && payload.stages) || {};
  let spans = {};

  TARA_FEEDBACK_STAGES.forEach((stage) => {
    const row = source[stage];
    if (!row || row.status === 'not_started') {
      stages[stage] = { ...EMPTY };
      return;
    }

    if (row.spans && typeof row.spans === 'object') {
      spans = { ...spans, ...row.spans };
    }

    if (row.status === 'in_progress') {
      // The server says when the work began. Timing it from this moment would
      // restart the clock every time the panel mounts, and the diagnostic now
      // starts at submission, so a student arriving here finds a run that is
      // already minutes old.
      stages[stage] = { ...EMPTY, phase: 'running', startedAt: startedAtFrom(row) };
      return;
    }

    if (row.status === 'succeeded') {
      if (!row.student_facing_feedback) {
        warnings.push(`${stage} completed without any feedback to show`);
        stages[stage] = { ...EMPTY, phase: 'error', error: { status: 500 } };
        return;
      }
      stages[stage] = {
        phase: 'ready',
        data: row.student_facing_feedback,
        status_label: row.student_facing_feedback.status
          || row.status_label
          || null,
        error: null,
        startedAt: null,
        stale: Boolean(row.stale),
        promptVersion: row.prompt_version || null,
      };
      return;
    }

    stages[stage] = { ...EMPTY, phase: 'error', error: { status: 500 } };
  });

  // Stage 3 copies stage 1's mark. If they disagree the diagnostic is
  // authoritative and the disagreement is a bug worth surfacing in the console,
  // never two different numbers on one screen.
  const shown = markOf(stages.diagnostic);
  const reported = scoreOf(stages.performance);
  if (shown !== null && reported !== null && shown !== reported) {
    warnings.push(
      `performance report gives ${reported} but the diagnostic gave ${shown}; showing the diagnostic`,
    );
  }

  return { stages, spans, warnings };
}

function markOf(entry) {
  const result = entry && entry.data && entry.data.assessment_result;
  const mark = result && result.mark_out_of_100;
  return typeof mark === 'number' ? mark : null;
}

function scoreOf(entry) {
  const result = entry && entry.data && entry.data.result;
  const score = result && result.score_out_of_100;
  return typeof score === 'number' ? score : null;
}

export function taraDisplayMark(stages) {
  return markOf(stages && stages.diagnostic);
}
